import { createContext, useContext, useState, useRef, useCallback } from "react";

const NavGuardContext = createContext(null);

export function NavGuardProvider({ children }) {
  const guardRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [pendingKind, setPendingKind] = useState(null);

  const setGuard = useCallback((g) => { guardRef.current = g; }, []);

  const attempt = useCallback((action) => {
    const g = guardRef.current;
    if (g && g.dirty) { setPendingKind(g.kind || "report"); setPending(() => action); }
    else action();
  }, []);

  const close = () => { setPending(null); setPendingKind(null); };
  const run = (which) => {
    const g = guardRef.current;
    if (which === "save" && g?.onSaveDraft) g.onSaveDraft();
    if (which === "discard" && g?.onDiscard) g.onDiscard();
    const action = pending;
    setPending(null);
    setPendingKind(null);
    if (action) action();
  };

  return (
    <NavGuardContext.Provider value={{ setGuard, attempt }}>
      {children}
      {pending && pendingKind === "contact" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="exit-contact-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">¿Salir del mensaje?</h3>
            <p className="text-sm text-[#475569]">Tienes información ingresada que aún no ha sido enviada. Si sales ahora, se perderán los cambios.</p>
            <div className="space-y-2 pt-1">
              <button onClick={close} data-testid="exit-contact-keep"
                className="w-full h-12 rounded-xl bg-[#003893] text-white font-semibold">Seguir escribiendo</button>
              <button onClick={() => run("discard")} data-testid="exit-contact-discard"
                className="w-full h-12 rounded-xl bg-white border border-[#FCA5A5] text-[#991B1B] font-semibold">Salir sin enviar</button>
            </div>
          </div>
        </div>
      )}
      {pending && pendingKind !== "contact" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="exit-report-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            <h3 className="font-heading text-lg font-bold text-[#0F172A]">¿Salir del reporte?</h3>
            <p className="text-sm text-[#475569]">Tienes información ingresada que aún no ha sido enviada. Puedes guardar el borrador para completarlo después o salir y descartar los cambios.</p>
            <div className="space-y-2 pt-1">
              <button onClick={close} data-testid="exit-keep-editing"
                className="w-full h-12 rounded-xl bg-[#1E3A8A] text-white font-semibold">Seguir editando</button>
              <button onClick={() => run("save")} data-testid="exit-save-draft"
                className="w-full h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold">Guardar borrador y salir</button>
              <button onClick={() => run("discard")} data-testid="exit-discard"
                className="w-full h-12 rounded-xl bg-white border border-[#FCA5A5] text-[#991B1B] font-semibold">Salir sin guardar</button>
            </div>
          </div>
        </div>
      )}
    </NavGuardContext.Provider>
  );
}

export function useNavGuard() {
  return useContext(NavGuardContext) || { setGuard: () => {}, attempt: (a) => a() };
}
